import {observer} from 'mobx-react-lite';
import moment from 'moment';
import React, {useCallback, useContext, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {
  EButton,
  EIconButton,
  ESmallButton,
  ESwitch,
} from 'webapp-lib/Components';

import {
  ActivitySharedNotification,
  DocumentSharedNotification,
  FeeDeletedNotification,
  FeesShouldBeCompletedNotification,
  InaccessibleActivityCreatedNotification,
  InformativeObligationVersionDeadlineReachedNotification,
  InvoiceContainerCompleteByDueDateReachedNotification,
  InvoiceContainerDueDateReachedNotification,
  InvoiceReminderDueDateReachedNotification,
  JustitiaSubmissionReceivedNotification,
  Notification,
  ObligationReminderTimestampReachedNotification,
  ObligationVersionApprovedNotification,
  ObligationVersionCreatedNotification,
  ObligationVersionRejectedNotification,
  ObligationVersionReviewNeededNotification,
  RemovedAsResponsiblePersonOfTheCasusNotification,
  ResolvableObligationVersionDeadlineReachedNotification,
  SetAsResponsiblePersonOfTheCasusNotification,
  TaskAssignedNotification,
  TaskAssigneeRemovedNotification,
  TaskCompletedNotification,
  TaskDeletedNotification,
  TaskDueDateChangedNotification,
  TaskDueDateReachedNotification,
  TaskOwnerChangedNotification,
  TaskOwnerRemovedNotification,
  TaskReminderDateReachedNotification,
  TaskReopenedNotification,
  ZipFileCreatedNotification,
  ZipFileCreationFailedNotification,
} from '@lexian/api/notifications-api';
import {
  CasusIcon,
  ChevronLargeLeft,
  Clock,
  Delete,
  FinanceIcon,
  Folder,
  GenericDocument,
  Obligation,
  Task,
} from '_/assets/images';
import {I18NextNS} from '_/i18n';
import {getRouteUrl} from '_/utilities';

import {StoreContext} from '../store';
import {downloadZipFile} from '../store/store';
import {SetVisibilityContext} from '../utilities/context';

import {NotificationCustonDateInputForm} from './NotificationCustomDateInputForm';
import {
  InformativeNotification,
  ResolvableObligationVersionDeadlineReached,
  TaskDueDateReached,
} from './entites';
import {
  ButtonWrapper,
  DateFormatWrapper,
  DelayHeading,
  DelayOptions,
  NotificationContent,
  NotificationHeader,
} from './styles';

type Props = {
  className?: string;
  notification: Notification;
  onBack: () => void;
};

type NotificationProps = {
  notification: Notification;
  action?: React.ReactElement;
  Icon?: React.ReactElement;
};

const NotificationElement = ({
  notification,
  Icon,
  action,
}: NotificationProps) => {
  if (isTaskDueDateReachedNotification(notification)) {
    return <TaskDueDateReached notification={notification} />;
  }
  if (isResolvableObligationVersionDeadlineReachedNotification(notification)) {
    return (
      <ResolvableObligationVersionDeadlineReached notification={notification} />
    );
  }
  return (
    <InformativeNotification
      action={action}
      Icon={Icon}
      notification={notification}
    />
  );
};

const isDocumentSharedNotification = (
  notification: Notification
): notification is DocumentSharedNotification =>
  notification.name === 'DocumentSharedNotification';

const isObligationVersionReviewNeededNotification = (
  notification: Notification
): notification is ObligationVersionReviewNeededNotification =>
  notification.name === 'ObligationVersionReviewNeededNotification';

const isRemovedAsResponsiblePersonOfTheCasusNotification = (
  notification: Notification
): notification is RemovedAsResponsiblePersonOfTheCasusNotification =>
  notification.name === 'RemovedAsResponsiblePersonOfTheCasusNotification';

const isSetAsResponsiblePersonOfTheCasusNotification = (
  notification: Notification
): notification is SetAsResponsiblePersonOfTheCasusNotification =>
  notification.name === 'SetAsResponsiblePersonOfTheCasusNotification';

const isTaskOwnerChangedNotification = (
  notification: Notification
): notification is TaskOwnerChangedNotification =>
  notification.name === 'TaskOwnerChangedNotification';

const isTaskAssignedNotification = (
  notification: Notification
): notification is TaskAssignedNotification =>
  notification.name === 'TaskAssignedNotification';

const isTaskDueDateReachedNotification = (
  notification: Notification
): notification is TaskDueDateReachedNotification =>
  notification.name === 'TaskDueDateReachedNotification';

const isTaskReminderDateReachedNotification = (
  notification: Notification
): notification is TaskReminderDateReachedNotification =>
  notification.name === 'TaskReminderDateReachedNotification';

const isTaskOwnerRemovedNotification = (
  notification: Notification
): notification is TaskOwnerRemovedNotification =>
  notification.name === 'TaskOwnerRemovedNotification';

const isTaskAssigneeRemovedNotification = (
  notification: Notification
): notification is TaskAssigneeRemovedNotification =>
  notification.name === 'TaskAssigneeRemovedNotification';

const isTaskDeletedNotification = (
  notification: Notification
): notification is TaskDeletedNotification =>
  notification.name === 'TaskDeletedNotification';

const isTaskCompletedNotification = (
  notification: Notification
): notification is TaskCompletedNotification =>
  notification.name === 'TaskCompletedNotification';

const isTaskReopenedNotification = (
  notification: Notification
): notification is TaskReopenedNotification =>
  notification.name === 'TaskReopenedNotification';

const isTaskDueDateChangedNotification = (
  notification: Notification
): notification is TaskDueDateChangedNotification =>
  notification.name === 'TaskDueDateChangedNotification';

const isActivitySharedNotification = (
  notification: Notification
): notification is ActivitySharedNotification =>
  notification.name === 'ActivitySharedNotification';

const isZipFileCreatedNotification = (
  notification: Notification
): notification is ZipFileCreatedNotification =>
  notification.name === 'ZipFileCreatedNotification';

const isZipFileCreationFailedNotification = (
  notification: Notification
): notification is ZipFileCreationFailedNotification =>
  notification.name === 'ZipFileCreationFailedNotification';

const isFeesShouldBeCompletedNotification = (
  notification: Notification
): notification is FeesShouldBeCompletedNotification =>
  notification.name === 'FeesShouldBeCompletedNotification';

const isInvoiceContainerCompleteByDueDateReachedNotification = (
  notification: Notification
): notification is InvoiceContainerCompleteByDueDateReachedNotification =>
  notification.name === 'InvoiceContainerCompleteByDueDateReachedNotification';

const isInvoiceContainerDueDateReachedNotification = (
  notification: Notification
): notification is InvoiceContainerDueDateReachedNotification =>
  notification.name === 'InvoiceContainerDueDateReachedNotification';

const isInvoiceReminderDueDateReachedNotification = (
  notification: Notification
): notification is InvoiceReminderDueDateReachedNotification =>
  notification.name === 'InvoiceReminderDueDateReachedNotification';

const isFeeDeletedNotification = (
  notification: Notification
): notification is FeeDeletedNotification =>
  notification.name === 'FeeDeletedNotification';

const isInaccessibleActivityCreatedNotification = (
  notification: Notification
): notification is InaccessibleActivityCreatedNotification =>
  notification.name === 'InaccessibleActivityCreatedNotification';

const isInformativeObligationVersionDeadlineReachedNotification = (
  notification: Notification
): notification is InformativeObligationVersionDeadlineReachedNotification =>
  notification.name ===
  'InformativeObligationVersionDeadlineReachedNotification';

const isObligationReminderTimestampReachedNotification = (
  notification: Notification
): notification is ObligationReminderTimestampReachedNotification =>
  notification.name === 'ObligationReminderTimestampReachedNotification';

const isObligationVersionApprovedNotification = (
  notification: Notification
): notification is ObligationVersionApprovedNotification =>
  notification.name === 'ObligationVersionApprovedNotification';

const isObligationVersionCreatedNotification = (
  notification: Notification
): notification is ObligationVersionCreatedNotification =>
  notification.name === 'ObligationVersionCreatedNotification';

const isObligationVersionRejectedNotification = (
  notification: Notification
): notification is ObligationVersionRejectedNotification =>
  notification.name === 'ObligationVersionRejectedNotification';

const isResolvableObligationVersionDeadlineReachedNotification = (
  notification: Notification
): notification is ResolvableObligationVersionDeadlineReachedNotification =>
  notification.name ===
  'ResolvableObligationVersionDeadlineReachedNotification';

const isJustitiaSubmissionReceivedNotification = (
  notification: Notification
): notification is JustitiaSubmissionReceivedNotification =>
  notification.name === 'JustitiaSubmissionReceivedNotification';

const UNotificationView = ({
  className,
  notification,
  onBack,
}: Props): React.ReactElement => {
  const {t} = useTranslation<I18NextNS>('notifications');
  const store = useContext(StoreContext);
  const {close} = useContext(SetVisibilityContext);
  const navigate = useNavigate();
  const handleChange = useCallback(async () => {
    await store.setImportantFlag({
      newFlag: !notification.importantFlag,
      notificationId: notification.id,
    });
  }, [notification.id, notification.importantFlag, store]);
  const clearNotification = useCallback(() => {
    store.deleteNotification({notificationId: notification.id});
  }, [store, notification]);
  const [customFlag, setCustomFlag] = useState(false);

  const getNotificationProps = (
    icon: React.ReactElement,
    linkPath: string,
    customText?: string
  ) => ({
    Icon: icon,
    action: (
      <ButtonWrapper>
        <ESmallButton
          onClick={() => {
            close();
            navigate(linkPath);
          }}
        >
          {customText ?? t('common:actions.goToSource')}
        </ESmallButton>
      </ButtonWrapper>
    ),
    notification,
  });

  // eslint-disable-next-line complexity
  const elementProps = useMemo((): NotificationProps => {
    if (isDocumentSharedNotification(notification)) {
      return getNotificationProps(
        <GenericDocument />,
        getRouteUrl({
          module: 'casus-documents',
          casusId: notification.entity.casusId,
          folderId: notification.entity.folderId,
          documentId: notification.entity.id,
          clearFilters: true,
        })
      );
    } else if (
      isObligationVersionReviewNeededNotification(notification) ||
      isInformativeObligationVersionDeadlineReachedNotification(notification) ||
      isObligationVersionApprovedNotification(notification) ||
      isObligationVersionCreatedNotification(notification) ||
      isObligationVersionRejectedNotification(notification)
    ) {
      return getNotificationProps(
        <Obligation />,
        getRouteUrl({
          module: 'obligations',
          obligationId: notification.entity.id,
        })
      );
    } else if (isObligationReminderTimestampReachedNotification(notification)) {
      return getNotificationProps(
        <Obligation />,
        getRouteUrl({
          module: 'obligations',
          obligationId: notification.entity.obligationId,
        })
      );
    } else if (
      isResolvableObligationVersionDeadlineReachedNotification(notification) ||
      isTaskDueDateReachedNotification(notification)
    ) {
      return {notification};
    } else if (isTaskReminderDateReachedNotification(notification)) {
      const {entity} = notification;
      return entity
        ? getNotificationProps(
            <Task />,
            getRouteUrl({
              module: 'casus-tasks',
              casusId: entity.casusId,
              taskId: entity.id,
            })
          )
        : {Icon: <Task />, notification};
    } else if (
      isRemovedAsResponsiblePersonOfTheCasusNotification(notification) ||
      isSetAsResponsiblePersonOfTheCasusNotification(notification)
    ) {
      return getNotificationProps(
        <CasusIcon />,
        getRouteUrl({module: 'casus-overview', casusId: notification.entity.id})
      );
    } else if (
      isTaskOwnerChangedNotification(notification) ||
      isTaskAssignedNotification(notification) ||
      isTaskOwnerRemovedNotification(notification) ||
      isTaskAssigneeRemovedNotification(notification) ||
      isTaskCompletedNotification(notification) ||
      isTaskReopenedNotification(notification) ||
      isTaskDueDateChangedNotification(notification)
    ) {
      const {entity} = notification;
      return entity
        ? getNotificationProps(
            <Task />,
            getRouteUrl({
              module: 'casus-tasks',
              casusId: entity.casusId,
              taskId: entity.id,
              clearFilters: true,
            })
          )
        : {Icon: <Task />, notification};
    } else if (isTaskDeletedNotification(notification)) {
      const {entity} = notification;
      return entity
        ? getNotificationProps(
            <Task />,
            getRouteUrl({
              module: 'casus-tasks',
              casusId: entity.casusId,
              clearFilters: true,
            })
          )
        : {Icon: <Task />, notification};
    } else if (isActivitySharedNotification(notification)) {
      return getNotificationProps(
        <Clock />,
        getRouteUrl({
          module: 'time-tracking',
          dateValue: moment(notification.entity.day).valueOf(),
        })
      );
    } else if (isZipFileCreatedNotification(notification)) {
      return {
        Icon: <GenericDocument />,
        action: (
          <EButton
            onClick={(): void => {
              downloadZipFile({zipFileId: notification.entity.id});
            }}
          >
            {t('common:actions.download')}
          </EButton>
        ),
        notification,
      };
    } else if (isZipFileCreationFailedNotification(notification)) {
      return {
        Icon: <GenericDocument />,
        notification,
      };
    } else if (isFeesShouldBeCompletedNotification(notification)) {
      return getNotificationProps(
        <CasusIcon />,
        getRouteUrl({
          module: 'casus-fees',
          casusId: notification.entity.id,
          clearFilters: true,
        })
      );
    } else if (
      isInvoiceContainerCompleteByDueDateReachedNotification(notification)
    ) {
      return getNotificationProps(
        <FinanceIcon />,
        getRouteUrl({
          module: 'invoicing',
          view: 'invoice-containers',
          invoiceContainerId: notification.entity.id,
          clearFilters: true,
        })
      );
    } else if (isInvoiceContainerDueDateReachedNotification(notification)) {
      return getNotificationProps(
        <FinanceIcon />,
        getRouteUrl({
          module: 'invoicing',
          view: 'invoice-containers',
          invoiceContainerId: notification.entity.id,
          clearFilters: true,
        })
      );
    } else if (isInvoiceReminderDueDateReachedNotification(notification)) {
      return getNotificationProps(
        <FinanceIcon />,
        getRouteUrl({module: 'invoicing', view: 'invoices', clearFilters: true})
      );
    } else if (isFeeDeletedNotification(notification)) {
      return getNotificationProps(
        <FinanceIcon />,
        getRouteUrl({module: 'casus-fees', casusId: notification.entity.id}),
        t('common:actions.goToCasus')
      );
    } else if (isInaccessibleActivityCreatedNotification(notification)) {
      return {
        Icon: <Clock />,
        action: undefined,
        notification,
      };
    } else if (isJustitiaSubmissionReceivedNotification(notification)) {
      return getNotificationProps(
        <Folder />,
        getRouteUrl({
          module: 'casus-documents-submissions',
          casusId: notification.entity.casusId,
          submissionId: notification.entity.id,
          reloadSubmissionFolders: true,
        })
      );
    } else {
      throw new Error('Unknown notification');
    }
    // getNotifcationProps can not be a dependencies as it is not wrapped in a callback
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notification, t]);

  const handleMinutesOnClick = useCallback(
    (numberOfMinutes: number) => {
      store.setDelay({
        newDate: moment().add(numberOfMinutes, 'minutes').utc().format(),
        notificationId: notification.id,
      });
    },
    [notification.id, store]
  );

  const handleCustomClick = useCallback(() => {
    setCustomFlag((currentState) => !currentState);
  }, []);

  return (
    <>
      <NotificationHeader>
        <EIconButton onClick={onBack}>
          <ChevronLargeLeft />
        </EIconButton>
        <DateFormatWrapper>
          {moment(notification.notifyAt).diff(moment(), 'days') === 0
            ? moment(notification.notifyAt).format('HH:mm')
            : t('common:dateFormat.withoutTime', {
                date: new Date(notification.notifyAt!),
              })}
        </DateFormatWrapper>
        <EIconButton onClick={clearNotification}>
          <Delete />
        </EIconButton>
      </NotificationHeader>
      <NotificationContent className={className}>
        <ESwitch
          checked={notification.importantFlag}
          label={t('notificationView.markAsImportant')}
          onChange={handleChange}
        />
        <NotificationElement {...elementProps} />
      </NotificationContent>
      <div>
        <DelayHeading>{t('notificationView.delay.title')}</DelayHeading>
        <DelayOptions>
          <ESmallButton
            onClick={() => {
              handleMinutesOnClick(5);
            }}
          >
            {t('notificationView.delay.fiveMinutes')}
          </ESmallButton>
          <ESmallButton
            onClick={() => {
              handleMinutesOnClick(30);
            }}
          >
            {t('notificationView.delay.thirtyMinutes')}
          </ESmallButton>
          <ESmallButton
            onClick={() => {
              handleMinutesOnClick(60);
            }}
          >
            {t('notificationView.delay.oneHour')}
          </ESmallButton>
          <ESmallButton onClick={handleCustomClick}>
            {t('notificationView.delay.custom')}
          </ESmallButton>
        </DelayOptions>
        {customFlag ? (
          <NotificationCustonDateInputForm notificationId={notification.id} />
        ) : null}
      </div>
    </>
  );
};

export const NotificationView = observer(UNotificationView);
